import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, Calendar, BarChart3, Settings, UserCheck, Trash2, Mail, Star, Phone, DollarSign, Award } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { TeamMemberModal } from '@/components/team/TeamMemberModal';
import { TeamScheduler } from '@/components/team/TeamScheduler';
import { TeamWorkload } from '@/components/team/TeamWorkload';
import { teamService } from '@/services/teamService';
import { providerService } from '@/services/providerService';
import type { TeamMember } from '@/types';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { FadeIn } from '@/components/ui/AnimatedComponents';

export function TeamPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'members' | 'schedule' | 'analytics'>('members');
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teamAnalytics, setTeamAnalytics] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | undefined>();
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');

  const isProvider = user?.role === 'provider';
  const isCompanyProvider = isProvider && user ? providerService.isCompanyProvider(user as any) : false;

  useEffect(() => {
    if (!isProvider || !isCompanyProvider) {
      if (user) {
        // If loaded but not company, maybe redirect. For now, assume logic holds.
      }
    } else {
      loadTeamData();
    }
  }, [user?.id, isProvider, isCompanyProvider]);

  const loadTeamData = async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      const [membersResult, analyticsResult] = await Promise.allSettled([
        teamService.getTeamMembers(user.id),
        teamService.getTeamAnalytics(user.id)
      ]);

      if (membersResult.status === 'fulfilled') {
        setTeamMembers(membersResult.value);
      } else {
        setTeamMembers([]);
      }

      if (analyticsResult.status === 'fulfilled') {
        setTeamAnalytics(analyticsResult.value);
      } else {
        setTeamAnalytics(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddMember = () => {
    setSelectedMember(undefined);
    setModalMode('add');
    setShowMemberModal(true);
  };

  const handleEditMember = (memberId: string) => {
    const member = teamMembers.find(m => m.id === memberId);
    if (member) {
      setSelectedMember(member);
      setModalMode('edit');
      setShowMemberModal(true);
    }
  };

  const handleSaveMember = async (memberData: Omit<TeamMember, 'id' | 'providerId' | 'createdAt' | 'updatedAt'>) => {
    try {
      if (modalMode === 'add') {
        await teamService.addTeamMember(user?.id || '', memberData);
      } else if (selectedMember) {
        await teamService.updateTeamMember(selectedMember.id, memberData);
      }
      await loadTeamData();
      setShowMemberModal(false);
    } catch (error) {
      // Failed to save member
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (confirm('Are you sure you want to remove this team member?')) {
      try {
        await teamService.removeTeamMember(memberId);
        await loadTeamData();
      } catch (error) {
        // Failed to remove member
      }
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 pb-20">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg min-h-touch">
        Skip to main content
      </a>
      <main id="main-content" role="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Breadcrumb items={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Team Management' }]} />

        <div className="page-header mt-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="page-title">Team Management</h1>
              <p className="page-description">Manage your team members, schedules, and performance.</p>
            </div>
            <div className="flex gap-3">
              <Button icon={<Plus className="w-4 h-4" />} onClick={handleAddMember}>Add Member</Button>
            </div>
          </div>
        </div>

        {/* Stats */}
        {teamAnalytics && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <FadeIn delay={0}>
              <Card className="p-6 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Total Staff</p>
                    <p className="text-2xl font-bold text-gray-900">{teamAnalytics.totalMembers}</p>
                  </div>
                  <div className="p-3 bg-primary-50 rounded-lg text-primary-600">
                    <Users className="w-6 h-6" />
                  </div>
                </div>
              </Card>
            </FadeIn>
            <FadeIn delay={50}>
              <Card className="p-6 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Active Duty</p>
                    <p className="text-2xl font-bold text-gray-900">{teamAnalytics.activeMembers}</p>
                  </div>
                  <div className="p-3 bg-emerald-50 rounded-lg text-emerald-600">
                    <UserCheck className="w-6 h-6" />
                  </div>
                </div>
              </Card>
            </FadeIn>
            <FadeIn delay={100}>
              <Card className="p-6 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Total Jobs</p>
                    <p className="text-2xl font-bold text-gray-900">{teamAnalytics.totalJobsCompleted}</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg text-blue-600">
                    <Award className="w-6 h-6" />
                  </div>
                </div>
              </Card>
            </FadeIn>
            <FadeIn delay={150}>
              <Card className="p-6 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Avg Rating</p>
                    <p className="text-2xl font-bold text-gray-900">{teamAnalytics.averageTeamRating.toFixed(1)}</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg text-amber-600">
                    <Star className="w-6 h-6" />
                  </div>
                </div>
              </Card>
            </FadeIn>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('members')}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'members' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              <Users className="w-4 h-4" /> Team Members
            </button>
            <button
              onClick={() => setActiveTab('schedule')}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'schedule' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              <Calendar className="w-4 h-4" /> Schedule
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'analytics' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              <BarChart3 className="w-4 h-4" /> Performance
            </button>
          </div>

          <div className="p-6">
            {activeTab === 'members' && (
              <div className="space-y-6">
                {teamMembers.length > 0 ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {teamMembers.map((member) => (
                      <Card key={member.id} className="flex flex-col">
                        <div className="p-6 flex flex-col sm:flex-row gap-6">
                          <div className="flex-shrink-0">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center text-gray-500 border border-gray-200">
                              <Users className="w-8 h-8" />
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="text-lg font-bold text-gray-900">{member.firstName} {member.lastName}</h3>
                                <p className="text-sm text-gray-500 capitalize">{member.role.toLowerCase()}</p>
                              </div>
                              <Badge variant={member.isActive ? 'success' : 'secondary'}>{member.isActive ? 'Active' : 'Inactive'}</Badge>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 mt-4 text-sm">
                              <div className="flex items-center gap-2 text-gray-600">
                                <Mail className="w-4 h-4 text-gray-400" /> {member.email}
                              </div>
                              <div className="flex items-center gap-2 text-gray-600">
                                <Phone className="w-4 h-4 text-gray-400" /> {member.phone}
                              </div>
                              <div className="flex items-center gap-2 text-gray-600">
                                <DollarSign className="w-4 h-4 text-gray-400" /> {formatCurrency(member.hourlyRate || 0)}/hr
                              </div>
                            </div>

                            <div className="mt-4">
                              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Skills</div>
                              <div className="flex flex-wrap gap-2">
                                {member.skills.map(skill => (
                                  <span key={skill} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md border border-gray-200">{skill}</span>
                                ))}
                                {member.skills.length === 0 && <span className="text-xs text-gray-400 italic">No skills listed</span>}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gray-50 px-6 py-3 border-t border-gray-100 flex justify-end gap-2 rounded-b-lg mt-auto">
                          <Button size="sm" variant="ghost" onClick={() => handleEditMember(member.id)} icon={<Settings className="w-4 h-4" />}>Configure</Button>
                          <Button size="sm" variant="ghost" onClick={() => handleRemoveMember(member.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" icon={<Trash2 className="w-4 h-4" />}>Remove</Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900">No team members</h3>
                    <p className="text-gray-500 mb-6">Get started by adding your first team member.</p>
                    <Button onClick={handleAddMember}>Add Member</Button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'schedule' && (
              <TeamScheduler teamMembers={teamMembers} onScheduleUpdate={loadTeamData} />
            )}

            {activeTab === 'analytics' && (
              <TeamWorkload teamMembers={teamMembers} />
            )}
          </div>
        </div>
      </main>

      <TeamMemberModal
        isOpen={showMemberModal}
        onClose={() => setShowMemberModal(false)}
        onSave={handleSaveMember}
        member={selectedMember}
        mode={modalMode}
      />
    </div>
  );
}